import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SectionLabel } from "../../components/ui";
import { supabase } from "../../auth/supabase";

const inputClass =
  "w-full box-border px-[16px] py-[15px] rounded-[13px] bg-white border border-line " +
  "text-[15px] text-ink placeholder:text-ink4/70 outline-none focus:border-ink";

/**
 * 透過 Supabase 重置密碼郵件點擊後的頁面：
 * Supabase 會將 code/hash 放在 URL  fragment，supabase-js 會自動
 * 從 URL 解析並設置 session，此頁負責收集新密碼並更新。
 */
export default function UpdatePassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  // 在元件掛載時驢 supabase-js 處理 URL 中的 auth 參數
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setReady(true);
      } else {
        setError("此連結已過期或無效，請重新申請密碼重置。");
      }
    };
    void checkSession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setInfo("");

    if (password.length < 6) {
      setError("密碼至少需要 6 個字元");
      return;
    }
    if (password !== confirmPassword) {
      setError("兩次輸入的密碼不一致");
      return;
    }

    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setError(error.message);
      } else {
        setInfo("密碼已更新，正在前往今日頁面…");
        setTimeout(() => navigate("/today", { replace: true }), 1500);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-dvh box-border bg-cream flex flex-col px-[max(26px,5vw)] pt-[60px] pb-[34px]">
      <SectionLabel className="mt-[20px]">MANGO WORD · 帳戶</SectionLabel>
      <h1 className="mt-[14px] mb-0 text-[32px] leading-[1.25] font-bold text-ink tracking-[-.01em]">
        設定新密碼
      </h1>

      {!ready ? (
        <p className="mt-[16px] text-[15px] text-ink4">驗證重置連結中…</p>
      ) : (
        <form onSubmit={handleSubmit} className="mt-[28px] flex flex-col gap-[13px]" noValidate>
          <label className="flex flex-col gap-2 text-[12px] font-medium text-ink3">
            新密碼
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              aria-label="新密碼"
              className={inputClass}
              placeholder="至少 6 個字元"
            />
          </label>
          <label className="flex flex-col gap-2 text-[12px] font-medium text-ink3">
            確認密碼
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              aria-label="確認密碼"
              className={inputClass}
              placeholder="再次輸入"
            />
          </label>

          {error && <div role="alert" className="text-[13.5px] text-accent-deep">{error}</div>}
          {info && <div role="status" className="text-[13.5px] text-ink">{info}</div>}

          <button
            type="submit"
            disabled={busy}
            aria-busy={busy}
            className="mt-[6px] w-full rounded-full py-4 text-[16px] font-bold cursor-pointer bg-ink text-cream disabled:opacity-50"
          >
            {busy ? "處理中…" : "更新密碼"}
          </button>
        </form>
      )}
    </div>
  );
}
